import React, { ComponentProps, useContext, useEffect, useState } from "react";
import { DatePicker } from "react-date-picker";
import styled from "styled-components";

import { toAWSDate } from "@examspark/common/dist/utils/coursePlanner";

import { FormContext } from "../../contexts/FormContext";
import Button from "../buttons/Button";
import { IconCalendarLine, IconRefreshLine } from "../icons";

import { StyledDateInput, awsDateToDatePickerValue } from "./DateInput";

export type StringValuePiece = string | null;
export type StringValue =
  | StringValuePiece
  | [StringValuePiece, StringValuePiece];

const StyledCombo = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
`;

const DateComboInput = ({
  start,
  end,
  disabled,
  onChange,
  // @ts-expect-error our types are bad
  defaultActiveStartDate,
  ...sharedDatePickerProps
}: {
  start: string | null;
  end: string | null;
  disabled?: boolean;
  onChange?: (value: StringValue) => void;
} & Omit<ComponentProps<typeof DatePicker>, "onChange">) => {
  const form = useContext(FormContext);

  const [startDate, endDate] = React.useMemo(
    () => [awsDateToDatePickerValue(start), awsDateToDatePickerValue(end)],
    [start, end],
  );

  // We want to control which month is actively displayed
  // so we can set it to whatever the last 'logical' date is
  // from elsewhere in the form (ie, set the active view to the endAt of the
  // last grading period, etc.)
  const [activeStartDate, setActiveStartDate] = useState<Date | null>();
  const [activeEndDate, setActiveEndDate] = useState<Date | null>();
  useEffect(() => {
    if (activeStartDate == null) {
      if (startDate != null) {
        setActiveStartDate(startDate);
      }

      if (defaultActiveStartDate != null) {
        setActiveStartDate(defaultActiveStartDate);
      }
    }
  }, [activeStartDate, defaultActiveStartDate, startDate]);

  useEffect(() => {
    if (activeEndDate == null) {
      if (endDate != null) {
        setActiveEndDate(endDate);
        return;
      }

      if (startDate != null) {
        setActiveEndDate(startDate);
        return;
      }
    }
  }, [activeEndDate, endDate, startDate]);

  // React-calendar does not send change events when the date is out of bounds
  // (but does update the UI) so we need to manually check and update the value
  const { minDate, maxDate } = sharedDatePickerProps;
  useEffect(() => {
    if (minDate != null && start != null && toAWSDate(minDate) > start) {
      onChange?.([toAWSDate(minDate), end]);
    }
  }, [end, minDate, onChange, start]);

  useEffect(() => {
    if (maxDate != null && end != null && toAWSDate(maxDate) < end) {
      onChange?.([start, toAWSDate(maxDate)]);
    }
  }, [end, maxDate, onChange, start]);

  return (
    <StyledCombo>
      <StyledDateInput>
        <DatePicker
          showFixedNumberOfWeeks
          disabled={disabled ?? form.disabled}
          activeStartDate={activeStartDate ?? undefined}
          onActiveStartDateChange={(viewData: any) => {
            setActiveStartDate(viewData.activeStartDate);
          }}
          calendarIcon={<IconCalendarLine height="16" />}
          // @ts-expect-error our types are bad
          clearIcon={null}
          locale="en-US"
          minDetail="year"
          value={startDate}
          // @ts-expect-error our types are bad
          onChange={(value: Date | null) => {
            // this is a hack but it prevents us getting dates
            // while the user is still trying to type
            if (value && value.getFullYear() < 1000) return;
            onChange?.([value ? toAWSDate(value) : null, end]);
          }}
          {...sharedDatePickerProps}
        />
      </StyledDateInput>
      <StyledDateInput>
        <DatePicker
          showFixedNumberOfWeeks
          disabled={disabled ?? form.disabled}
          activeStartDate={activeEndDate ?? undefined}
          onActiveStartDateChange={(viewData: any) => {
            setActiveEndDate(viewData.activeStartDate);
          }}
          calendarIcon={<IconCalendarLine height="16" />}
          // @ts-expect-error our types are bad
          clearIcon={null}
          locale="en-US"
          minDetail="year"
          value={endDate}
          // @ts-expect-error our types are bad
          onChange={(value: Date | null) => {
            // this is a hack but it prevents us getting dates
            // while the user is still trying to type
            if (value && value.getFullYear() < 1000) return;
            onChange?.([start, value ? toAWSDate(value) : null]);
          }}
          {...sharedDatePickerProps}
        />
      </StyledDateInput>
      <Button
        title="Reset Field"
        onClick={() => {
          onChange?.([null, null]);
          setActiveEndDate(null);
          setActiveStartDate(null);
        }}
        variant="plain"
        icon={<IconRefreshLine />}
      />
    </StyledCombo>
  );
};

export default DateComboInput;
